import { TranslatePipe } from '@ngx-translate/core';

import { ShortNumberPipe } from '@core-pipes/short-number/short-number.pipe';
import { CheckboxFilterValue } from '@search/components/checkbox-filter/models/checkbox-filter-value';
import { DatePipe } from '@angular/common';
import { DATE_PIPE_DATE_FORMAT } from '@core-controls/constants/date-format.constants';

export class SearchFilterTitlesService {
    constructor(private readonly translatePipe: TranslatePipe) { }

    public createRangeFilterTitle(
        defaultLocalizationKey: string,
        value: { min?: number, max?: number },
        upToTitleKey: string,
        isCurrency = true
    ): string {
        const translatedDefault = this.translatePipe.transform(defaultLocalizationKey) as string;

        if (value == null) {
            return translatedDefault;
        }

        const title = this.getRangeTitle(value, upToTitleKey, isCurrency);

        return title != null ? title : translatedDefault;
    }

    public createDateRangeFilterTitle(
        defaultLocalizationKey: string,
        value: { start?: Date, end?: Date },
        upToTitleKey: string
    ): string {
        const translatedDefault = this.translatePipe.transform(defaultLocalizationKey) as string;

        if (value == null) {
            return translatedDefault;
        }

        const title = this.getDateRangeTitle(value, upToTitleKey);

        return title != null ? title : translatedDefault;
    }

    public createCheckboxFilterTitle(
        originalValues: CheckboxFilterValue[],
        defaultLocalizationKey: string,
        values: number[],
        exactMatch: boolean,
        useExactMatchDefaultLocalizationKey = true
    ): string {

        const translatedDefault = this.translatePipe.transform(defaultLocalizationKey) as string;

        if (values == null || values.length === 0) {
            return translatedDefault;
        }

        if (exactMatch) {
            const title = values.reduce((title, value, index) => {
                const valueLocalizationKey = originalValues.find(originalValue => originalValue.value === value)?.valueLocalizationKey;

                if (valueLocalizationKey != null) {
                    const valueTranslation = this.translatePipe.transform(valueLocalizationKey) as string;

                    return title + (index === 0 ? valueTranslation : `, ${valueTranslation}`);
                }

                return title;
            }, '');

            return useExactMatchDefaultLocalizationKey ? `${title} ${translatedDefault}` : title;
        }

        const firstValueLocalizationKey = originalValues.find(originalValue => originalValue.value === values[0])?.valueLocalizationKey;
        const firstTranslatedValue = this.translatePipe.transform(firstValueLocalizationKey) as string;

        return `${firstTranslatedValue}+ ${translatedDefault}`;
    }

    private getRangeTitle(rangeValue: { min?: number, max?: number }, upToTitleKey: string, isCurrency = true): string {
        const divider = (rangeValue?.min && rangeValue.max)
            ? ' - '
            : rangeValue?.max != null
                ? ' '
                : '';

        return rangeValue == null || rangeValue.min == null && rangeValue.max == null
            ? null
            : `${this.getNumberTitle(rangeValue.min, upToTitleKey, isCurrency)}${divider}${this.getNumberTitle(rangeValue.max, '+', isCurrency)}`;
    }

    private getDateRangeTitle(rangeValue: { start?: Date, end?: Date }, upToTitleKey: string): string {
        const divider = (rangeValue?.start && rangeValue.end)
            ? ' - '
            : rangeValue?.end != null
                ? ' '
                : '';

        return rangeValue == null || rangeValue.start == null && rangeValue.end == null
            ? null
            : `${this.getDateTitle(rangeValue.start, upToTitleKey)}${divider}${this.getDateTitle(rangeValue.end, '+')}`;
    }

    private getDateTitle(value: Date, upToTitleKey: string): string {
        return value == null
            ? this.translatePipe.transform(upToTitleKey) as string
            : new DatePipe('en-US').transform(value, DATE_PIPE_DATE_FORMAT);
    }

    private getNumberTitle(value: number, upToTitleKey: string, isCurrency = true): string {
        return value == null
            ? this.translatePipe.transform(upToTitleKey) as string
            : new ShortNumberPipe().transform(value, isCurrency);
    }
}
